/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { SENTRY_ENVIRONMENT, REGION_CODE, RELEASE, SENTRY_DSN, SENTRY_ON } from '@gh/config';
import { first$ } from '@gh/rx/operators';
import { isEmpty, omitBy } from 'lodash';
import { UserContext } from './user';
import { UserData } from '@gh/common-shared-data';

if (typeof window !== 'undefined') {
  const originalError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (typeof message === 'string' && 
        (message.includes('ResizeObserver loop') || 
         message.includes('ResizeObserver loop completed with undelivered notifications'))) {
      return true;
    }
    if (originalError) {
      return originalError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || '';
    if (errorMessage.includes('ResizeObserver loop') || 
        errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.preventDefault();
    }
  });
}

if (SENTRY_ON) {
  window.onload = () => {
    const sentry = (window as any)['Sentry'];
    if (!sentry) {
      return;
    }
    try {
      sentry.init(
        {
          dsn: SENTRY_DSN,
          environment: SENTRY_ENVIRONMENT,
          release: RELEASE,
          integrations: [
            sentry.browserTracingIntegration(),
            sentry.replayIntegration(),
          ],
          replaysSessionSampleRate: 0,
          tracesSampleRate: ((SENTRY_ENVIRONMENT as any)?.includes('dev')) ? 0.5 : 0.1,
        });
    } catch (error) {
      console.log(error);
    }
  };
}

export function defaultErrorHandler(): Provider[] {
  return SENTRY_ON ? [{ provide: ErrorHandler, useClass: SentryErrorHandler }] : [];
}

@Injectable()
export class SentryErrorHandler extends ErrorHandler {

  constructor(private userContext: UserContext) {
    super();

    this.userContext.onData
      .pipe(first$(Boolean))
      .subscribe((user: UserData) => {
        window['Sentry'].configureScope((scope) => {
          scope.setUser(omitBy(
            {
              id: user.id,
              username: user.fullName,
              email: user.login,
              locale: user.locale.toStandardString(),
            },
            isEmpty));
          if (user.backendBuildNumber) {
            scope.setTag('backendBuildNumber', user.backendBuildNumber);
          }
          scope.setTag('region', REGION_CODE);
        });

      });
  }

  handleError(error: any): void {
    const errorMessage = error?.message || error?.toString() || '';
    const isResizeObserverError = 
      errorMessage.includes('ResizeObserver loop') ||
      errorMessage.includes('ResizeObserver loop completed with undelivered notifications');

    if (isResizeObserverError) {
      return;
    }

    (window as any)['Sentry'].captureException(error.originalError || error);
    // without setTimeout Sentry hangs while sending error
    setTimeout(
      () => {
        throw error;
      },
      0);
  }
}
